import { simpleApi } from "~/common/API/simpleApi";
import { ElNotification } from "element-plus";

interface IGetChannelsParams {
  entityType?: string;
  status?: string[];
  purpose?: string[];
  userId?: number;
  createdAtFrom?: string;
  createdAtTo?: string;
  pageNumber?: number;
  pageSize?: number;
  perPage?: number;
  "sortDirections[]"?: string;
  "sortFields[]"?: string;
  role?: string | number | string[] | number[];
  "status[]"?: string[];
  "entityId[]"?: number[];
}

interface IUniversalPlayloads extends IGetChannelsParams {
  id?: number;
  name?: string;
  telegramId?: string;
  email?: string;
  reason?: string;
}

interface ICreateUserPayload {
  email?: string;
  password?: string;
  role: number[];
  telegram: string;
  telegramId?: number | null;
}

interface IGetOrdersCounts {
  status?: string;
  publicationsStatus?: string[];
  createdAtFrom?: string;
  createdAtTo?: string;
}

interface ModerationResponse {
  success: boolean;
  message: string;
  data?: any;
}

type RequestMethods = "GET" | "POST" | "PUT" | "DELETE";

interface ApiCallOptions {
  method?: RequestMethods;
  body?: any;
  params?: any;
  query?: string;
}

// 1. Создать общий тип для ответа API
type ApiResponse<T> = {
  data: T;
  error?: string;
  success: boolean;
};

// 2. Создать общий обработчик ошибок
const handleApiError = (error: unknown, message: string) => {
  console.error(error);
  ElNotification({
    title: "Ошибка",
    message: message,
    type: "error",
  });
  return null;
};

// 3. Создать обертку для API-вызовов
const apiCall = async <T>(
  endpoint: string,
  options: ApiCallOptions
): Promise<ApiResponse<T> | null> => {
  try {
    const response = (await simpleApi(endpoint, options)) as T;
    return { data: response, success: true };
  } catch (error) {
    return handleApiError(error, "Попробуйте позже или свяжитесь с администратором");
  }
};

const ENDPOINTS = {
  MODERATION: {
    SEARCH: "/moderation/search",
    APPROVE: "/moderation/approve",
    REJECT: "/moderation/reject",
    REWORK: "/moderation/rework",
    DELETE: "/moderation/delete",
    COUNT: "/moderation/count",
  },
  ADMIN: {
    USERS: "/admin/users",
    ORDERS: "/admin/orders",
    // ... другие эндпоинты
  },
} as const;

export default class ModerationService {
  static getChannels = async (data: { params?: IGetChannelsParams; query?: string } = {}) => {
    const { params, query } = data;
    return apiCall(ENDPOINTS.MODERATION.SEARCH, {
      params: {
        ...params,
        ...(query ? { query } : {}),
      },
    });
  };

  /**
   * Подтверждает модерацию канала
   * @param channelId - ID канала для модерации
   * @returns Promise с результатом модерации
   */
  static confirmModerate = async (
    channelId: number
  ): Promise<ApiResponse<ModerationResponse> | null> => {
    if (!channelId) {
      return handleApiError(null, "Не указан ID канала");
    }

    const response = await apiCall<ModerationResponse>(ENDPOINTS.MODERATION.APPROVE, {
      method: "PUT",
      body: { id: channelId },
    });

    if (response?.success) {
      ElNotification({
        title: "Успешная модерация",
        message: "Канал успешно прошел модерацию",
        type: "success",
      });
    }

    return response;
  };

  // Метод для отклонения модерации канала
  static rejectModerate = async (payload: {
    rejectId: number;
    reason: string;
    reject?: boolean;
  }) => {
    // Определяем конечную точку API в зависимости от значения reject
    const endpoint = payload.reject ? "/moderation/reject" : "/moderation/rework";
    console.log("payload", payload);
    return new Promise((resolve) => {
      // Выполняем запрос к API с использованием simpleApi
      simpleApi(endpoint, { method: "PUT", body: { id: payload.rejectId, reason: payload.reason } })
        .then((res) => {
          // Уведомление об успешном отклонении модерации
          ElNotification({
            title: "Успешное отклонение",
            message: "Модерация отклонена",
            type: "success",
          });
          resolve(res); // Возвращаем ответ
        })
        .catch((err) => {
          console.error(err); // Логируем ошибку
          resolve(false); // Возвращаем false в случае ошибки
          // Уведомление об ошибке при отклонении модерации
          ElNotification({
            title: "Ошибка модерирования канала",
            message: "попробуйте чуть позже или свяжитесь с администратором...",
            type: "error",
          });
        });
    });
  };

  // Метод для получения количества запросов на модерацию
  static getModerationCount = async (data: { query?: string } = {}) => {
    const query = data.query || ""; // Получаем строку запроса или устанавливаем пустую строку, если она не задана
    const endpoint = `/moderation/count`; // Устанавливаем конечную точку API
    return new Promise((resolve) => {
      simpleApi(endpoint, { query: data.query }) // Выполняем запрос к API с использованием simpleApi
        .then((res) => {
          resolve(res); // Возвращаем ответ
        })
        .catch((err) => {
          console.error(err); // Логируем ошибку
          resolve(false); // Возвращаем false в случае ошибки
        });
    });
  };

  static getPublishers = async (data: { params?: IGetChannelsParams }) => {
    const endpoint = "/admin/users";
    return new Promise((resolve) => {
      simpleApi(endpoint, data)
        .then((res: any) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve([]);
        });
    });
  };

  static getAdvertisers = async (data: { params?: IGetChannelsParams }) => {
    // const endpoint = '/admin/user/advertisers?sortDirections[]=desc&sortFields[]=id';
    const endpoint = "/admin/users";
    return new Promise((resolve) => {
      simpleApi(endpoint, data)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve([]);
        });
    });
  };

  static getUsers = async (data: { params?: IGetChannelsParams }) => {
    try {
      const endpoint = ENDPOINTS.ADMIN.USERS;
      return await simpleApi(endpoint, data);
    } catch (error) {
      console.error("Ошибка при получении пользователей:", error);
      return [];
    }
  };

  static createUser = async (payload: ICreateUserPayload) => {
    const endpoint = "/admin/user/create";
    try {
      return await simpleApi(endpoint, {
        method: "POST",
        body: payload,
      });
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  static getAllChannels = async (data: { params?: IGetChannelsParams & { social?: string } }) => {
    const social = data.params?.social || "telegram";
    const endpoint = `/place/${social}/search?sortDirections[]=desc&sortFields[]=id`;
    return new Promise((resolve) => {
      simpleApi(endpoint, data)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve([]);
        });
    });
  };

  static getAllOrders = async (data: { params?: IGetChannelsParams }) => {
    const endpoint = `/admin/orders`;
    return new Promise((resolve) => {
      simpleApi(endpoint, data)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve([]);
        });
    });
  };

  static deleteModeration = async (payload: number) => {
    if (!payload) {
      ElNotification({
        title: "Ошибка удаления канала",
        message: "попробуйте чуть позже или свяжитесь с администратором...",
        type: "error",
      });
      return;
    }
    const endpoint = "/moderation/delete";
    return new Promise((resolve) => {
      simpleApi(endpoint, { method: "DELETE", body: { id: payload } })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve([]);
        });
    });
  };

  static updateAdmin = async (payload: {
    id: number;
    role: number[];
    name: string;
    email: string;
    telegram: string;
  }) => {
    const endpoint = "/admin/user/advertiser/update";
    return new Promise((resolve) => {
      simpleApi(endpoint, { method: "PUT", body: payload })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve([]);
        });
    });
  };

  static updatePublisher = async (payload: {
    id: number;
    name?: string;
    telegramId?: string;
    email?: string;
  }) => {
    const endpoint = "/admin/user/publisher/update";
    try {
      const response = await simpleApi(endpoint, { method: "PUT", body: payload });
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  static setPassword = async (payload: { id: number; password: string }) => {
    const endpoint = "/admin/user/set-password";
    try {
      return await simpleApi(endpoint, {
        method: "POST",
        body: payload,
      });
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  static publicationReject = async (payload: { reason?: string; id: number }) => {
    const endpoint = "/advert/publication/admin-reject";
    try {
      const response = await simpleApi(endpoint, { method: "PUT", body: payload });
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.log("Error 1", error);
      return null;
    }
  };

  static universalApi = async (
    payload: IUniversalPlayloads,
    endpoint: string,
    method: RequestMethods
  ) => {
    try {
      const response = await simpleApi(endpoint, { method: method, body: payload });
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.log("Error 1", error);
      return null;
    }
  };

  static getOrdersCounts = async (params: {
    status?: string;
  }): Promise<
    | {
        ordersCount: {
          status: string;
          count: number;
        }[];
      }
    | unknown
  > => {
    const endpoint = `/admin/order/count`;
    try {
      const response = await simpleApi(endpoint, { params });
      console.log("response", response);
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  static getPublicationsCounts = async (params: {
    advertOrderId?: number;
    advertCreativeId?: number;
    publisherId?: number;
    placeId?: number;
    "status[]"?: string[];
    "orderStatus[]"?: string[];
    "excludeStatus[]"?: string[];
    "excludeOrderStatus[]"?: string[];
    pageNumber?: number;
    perPage?: number;
    "sortFields[]"?: string[];
    "sortDirections[]"?: string[];
  }) => {
    const endpoint = "/admin/publication/count";

    const response = await simpleApi(endpoint, { params });
    return response;
  };

  static getAllPublishes = async (playload?: IGetChannelsParams) => {
    const endpoint = "/advert/publications";
    const params = {
      ...playload,
      "sortDirections[]": "desc",
      "sortFields[]": "id",
    };
    try {
      const response = await simpleApi(endpoint, { params });
      return response;
    } catch (error) {
      console.error(error);
      throw new Error("Ошибка при получении всех публикаций");
    }
  };

  static getAdminPublications = async (params: Record<string, any> = {}) => {
    const endpoint = "/admin/publications";
    try {
      return await simpleApi(endpoint, { params });
    } catch (error) {
      console.error("getAdminPublications", error);
      return null;
    }
  };

  static getOrderById = async (id: number) => {
    const endpoint = `/admin/order`;
    const params = {
      id: id,
    };
    try {
      return await simpleApi(endpoint, { params });
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  static deleteUserPaymentsData = async (
    id: number,
    type: "advertiser" | "publisher" = "publisher"
  ) => {
    const endpoint = "/admin/payments/details/clear";

    return await simpleApi(endpoint, {
      method: "POST",
      body: { id },
    });
  };

  static getUserPaymentsData = async (
    userId: number,
    type: "advertiser" | "publisher" = "publisher"
  ) => {
    let endpoint = "/admin/user/get-publisher-wallet";
    if (type === "advertiser") {
      endpoint = "/admin/user/get-advertiser-wallet";
    }

    try {
      const response: any = await simpleApi(endpoint, { params: { id: userId } });
      if (!response || response === "") return null;
      if (response?.error) return 0;

      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  static createPublisherWalletIndividual = async (payload: any) => {
    const endpoint = "/admin/payments/create-wallet-individual";
    return await simpleApi(endpoint, { method: "POST", body: payload });
  };

  static createPublisherWalletLegal = async (payload: any) => {
    const endpoint = "/admin/payments/create-wallet-legal";
    return await simpleApi(endpoint, { method: "POST", body: payload });
  };

  static updateChannel = async (payload: any) => {
    // Определяем endpoint в зависимости от социальной сети
    const social = payload?.social || "telegram";
    let endpoint = "/place/telegram/update";
    if (social === "vk") {
      endpoint = "/place/vk/update";
    } else if (social === "max") {
      endpoint = "/place/max/update";
    }
    return await simpleApi(endpoint, { method: "PUT", body: payload });
  };

  static createChannel = async (payload: {
    userId: number;
    social: string;
    url: string;
    name?: string;
    categoriesIds: number[];
    costs: {
      priceType: string;
      price: number;
    }[];
  }) => {
    const endpoint = "/admin/place/create";
    try {
      return await simpleApi(endpoint, { method: "POST", body: payload });
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  static updateChannelActive = async (payload: { id: number; isActive: boolean; social?: string }) => {
    const endpoint = "/admin/place/active";
    const response: any = await simpleApi(endpoint, {
      method: "PUT",
      body: {
        isActive: payload.isActive,
        byPlace: {
          social: payload.social || "telegram",
          id: payload.id,
        },
      },
    });
    if (response) {
      ElNotification({
        title: "Успешное изменение статуса",
        message: "Статус канала успешно изменен",
        type: "success",
      });
    }
    if (response?.error) {
      ElNotification({
        title: "Ошибка изменения статуса",
        message: "попробуйте чуть позже или свяжитесь с администратором...",
        type: "error",
      });
    }
    return response;
  };

  static deletePlace = async (payload: number, social?: string) => {
    const params = new URLSearchParams({ id: String(payload) });
    if (social) params.append("social", social);
    const endpoint = `/admin/place/?${params.toString()}`;
    try {
      const response: any = await simpleApi(endpoint, { method: "DELETE" });
      ElNotification({
        title: "Успешное удаление канала",
        message: "Канал успешно удален",
        type: "success",
      });
    } catch (error) {
      console.error(error);
      return null;
    }
    return;
  };

  // Import channels from file
  static importChannels = async (input: File | FormData) => {
    const endpoint = "/import/upload";

    const body =
      input instanceof FormData
        ? input
        : (() => {
            const fd = new FormData();
            fd.append("file", input);
            return fd;
          })();

    // console.log([...body.entries()])
    // return;

    return await simpleApi(endpoint, { method: "POST", body });
  };

  static getAllImports = async () => {
    const endpoint = "/import/search";
    const params = {
      perPage: 10,
      "sortFields[]": ["id"],
      "sortDirections[]": ["desc"],
    };

    return await simpleApi(endpoint, { method: "GET", params });
  };

  static getCountries = async () => {
    const endpoint = "/geo/countries";
    return await simpleApi(endpoint, { method: "GET" });
  };

  static getStatsOrdersCounts = async () => {
    const endpoint = "/admin/order/count";
    return await simpleApi(endpoint, { method: "GET" });
  };

  static getStatsTotalCost = async () => {
    const endpoint = "/admin/orders/total-coast";
    return await simpleApi(endpoint, { method: "GET" });
  };

  static checkSlug = async (slug: string) => {
    const endpoint = "/place/check-slug";
    return await simpleApi(endpoint, { method: "GET", params: { slug } });
  };

  static setNewRknData = async (id: number, isRegistered: boolean, social: string = "telegram") => {
    // Определяем endpoint в зависимости от социальной сети
    let endpoint = "/place/telegram/update";
    if (social === "vk") {
      endpoint = "/place/vk/update";
    } else if (social === "max") {
      endpoint = "/place/max/update";
    }
    
    try {
      ElNotification({
        title: "Успешное изменение статуса",
        message: "РКН статус успешно изменен",
        type: "success",
      });
      const response = await simpleApi(endpoint, {
        method: "PUT",
        body: { id, rknRegistration: { isRegistered } },
      });
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.error(error);
      ElNotification({
        title: "Ошибка изменения статуса",
        message: "попробуйте чуть позже или свяжитесь с администратором...",
        type: "error",
      });
      return null;
    }
  };

  static getGeoCountries = async () => {
    try {
      const response: any = await simpleApi("/geo/countries");
      return response?.records || [];
    } catch (error) {
      console.error("getGeoCountries", error);
      return [];
    }
  };

  static getGeoCities = async () => {
    try {
      const response: any = await simpleApi("/geo/cities");
      return response?.records || [];
    } catch (error) {
      console.error("getGeoCities", error);
      return [];
    }
  };
}
